import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import {
  Heart, Shield, Sparkles, ArrowRight, CheckCircle, Zap, Moon, Sun, Monitor,
  Brain, Smile, Bell, BookOpen, Lock, ClipboardList, Menu, X
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../lib/theme";
import InstallBanner from "../components/InstallBanner";

const PatientLandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");
  const { t, locale, setLocale } = useI18n();
  const { theme, cycleTheme } = useTheme();

  const registerUrl = refCode ? `/register?ref=${refCode}` : "/register?from=patient";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", "/manifest-patient.json");
    return () => { if (link) link.setAttribute("href", "/manifest.json"); };
  }, []);

  const BENEFITS = [
    { icon: ClipboardList, title: "Exercices entre les seances", desc: "Votre therapeute vous assigne des exercices personnalises a realiser a votre rythme, entre vos rendez-vous." },
    { icon: Smile, title: "Suivi de votre humeur", desc: "Notez votre humeur au quotidien. Votre therapeute suit votre evolution pour mieux vous accompagner." },
    { icon: Bell, title: "Rappels bienveillants", desc: "Si vous oubliez un exercice, un rappel par e-mail vous aide a rester sur la bonne voie." },
    { icon: BookOpen, title: "Ressources educatives", desc: "Acces a des modules de psychoeducation et des meditations guidees choisies par votre therapeute." },
    { icon: Brain, title: "16+ types d'exercices", desc: "Registre des pensees, restructuration cognitive, activation comportementale, pleine conscience et bien plus." },
    { icon: Lock, title: "Vos donnees protegees", desc: "Hebergement certifie HDS, consentement RGPD, et charte du secret professionnel signee par votre therapeute." },
  ];

  const STEPS = [
    { num: "1", title: "Recevez le lien", desc: "Votre therapeute vous partage un lien ou un QR code d'invitation." },
    { num: "2", title: "Creez votre compte", desc: "Inscrivez-vous en quelques secondes avec votre e-mail." },
    { num: "3", title: "Commencez vos exercices", desc: "Retrouvez vos exercices assigns et suivez votre progression." },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 shrink-0">
            <Link to={refCode ? `/bienvenue?ref=${refCode}` : "/bienvenue"}>
              <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-10 sm:h-12 w-auto" />
            </Link>
            <span className="font-heading text-lg sm:text-xl font-semibold leading-tight hidden sm:inline">Kintsugi TCC</span>
          </div>
          <div className="hidden md:flex items-center gap-2 sm:gap-3">
            <button onClick={cycleTheme} className="text-xs text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md border border-border" data-testid="patient-theme-toggle">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : theme === "light" ? <Moon className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
            </button>
            <button onClick={() => { const next = locale === "fr" ? "en" : locale === "en" ? "es" : "fr"; setLocale(next); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md border border-border" data-testid="patient-lang-toggle">
              {locale === "fr" ? "EN" : locale === "en" ? "ES" : "FR"}
            </button>
            <Button variant="ghost" onClick={() => navigate("/login?from=patient")} data-testid="patient-login-btn" className="text-sm">
              {t("auth.login")}
            </Button>
            <Button onClick={() => navigate(registerUrl)} data-testid="patient-register-btn" className="bg-primary hover:bg-primary/90 text-sm">
              Creer mon compte
            </Button>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-muted transition" data-testid="patient-mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md px-4 py-4 space-y-3 animate-fade-in" data-testid="patient-mobile-menu">
            <div className="flex items-center gap-2 py-1">
              <button onClick={cycleTheme} className="text-xs text-muted-foreground hover:text-foreground p-1.5 rounded-md border border-border">
                {theme === "dark" ? <Sun className="w-4 h-4" /> : theme === "light" ? <Moon className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
              </button>
              <button onClick={() => { const next = locale === "fr" ? "en" : locale === "en" ? "es" : "fr"; setLocale(next); }} className="text-xs text-muted-foreground hover:text-foreground p-1.5 rounded-md border border-border">
                {locale === "fr" ? "EN" : locale === "en" ? "ES" : "FR"}
              </button>
            </div>
            <Button variant="ghost" onClick={() => { navigate("/login?from=patient"); setMobileMenuOpen(false); }} className="w-full justify-center text-sm">
              {t("auth.login")}
            </Button>
            <Button onClick={() => { navigate(registerUrl); setMobileMenuOpen(false); }} className="w-full justify-center bg-primary hover:bg-primary/90 text-sm">
              Creer mon compte
            </Button>
          </div>
        )}
      </header>

      {/* ============ HERO ============ */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
            <Heart className="w-4 h-4" />
            Espace patient
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-foreground tracking-tight mb-6 leading-[1.15]">
            Votre therapeute vous accompagne
            <span className="text-primary"> entre les seances</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
            Kintsugi TCC est l'outil que votre therapeute utilise pour vous assigner des exercices, suivre votre progression et vous accompagner au quotidien dans votre parcours therapeutique.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" onClick={() => navigate(registerUrl)} data-testid="patient-hero-cta" className="bg-primary hover:bg-primary/90 gap-2">
              {refCode ? "Rejoindre mon therapeute" : "Creer mon compte"} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/login?from=patient")} data-testid="patient-hero-login">
              J'ai deja un compte
            </Button>
          </div>
          {refCode && (
            <p className="mt-4 text-sm text-primary font-medium" data-testid="patient-invitation-notice">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              Vous avez ete invite par votre therapeute — votre compte sera lie automatiquement.
            </p>
          )}
        </div>
      </section>

      {/* ============ COMMENT CA MARCHE ============ */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-10 text-center">Comment ca marche ?</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <div key={i} className="text-center" data-testid={`patient-step-${i}`}>
                <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  {step.num}
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ KINTSUGI SECTION ============ */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="patient-kintsugi-section">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/40 via-amber-100/20 to-transparent dark:from-amber-950/20 dark:via-amber-900/10 dark:to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t("landing.kintsugi.sectionTitle")}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4 text-foreground">
              {t("landing.kintsugi.subtitle")}
            </h2>
          </div>
          <div className="grid lg:grid-cols-5 gap-12 items-center mb-16">
            <div className="lg:col-span-2 flex justify-center animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="relative w-64 h-64 sm:w-72 sm:h-72">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-200/60 via-amber-300/30 to-amber-100/20 dark:from-amber-800/30 dark:via-amber-700/15 dark:to-amber-900/10" />
                <div className="absolute inset-3 rounded-full bg-card border border-amber-200/60 dark:border-amber-700/40 flex items-center justify-center shadow-lg">
                  <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="w-40 h-40 sm:w-44 sm:h-44 object-contain" />
                </div>
                <div className="absolute top-6 right-4 w-12 h-0.5 bg-gradient-to-r from-amber-400 to-transparent rotate-[30deg]" />
                <div className="absolute bottom-10 left-2 w-16 h-0.5 bg-gradient-to-l from-amber-400 to-transparent rotate-[-20deg]" />
                <div className="absolute top-1/2 right-0 w-10 h-0.5 bg-gradient-to-r from-amber-400 to-transparent rotate-[10deg]" />
              </div>
            </div>
            <div className="lg:col-span-3 space-y-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <p className="text-base text-muted-foreground leading-relaxed">{t("landing.kintsugi.paragraph1")}</p>
              <p className="text-base text-muted-foreground leading-relaxed">{t("landing.kintsugi.paragraph2")}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            {[
              { icon: Heart, title: t("landing.kintsugi.value1Title"), desc: t("landing.kintsugi.value1Desc"), color: "text-rose-500 dark:text-rose-400", bg: "bg-rose-50 dark:bg-rose-950/30" },
              { icon: Shield, title: t("landing.kintsugi.value2Title"), desc: t("landing.kintsugi.value2Desc"), color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/30" },
              { icon: Zap, title: t("landing.kintsugi.value3Title"), desc: t("landing.kintsugi.value3Desc"), color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30" },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-card border border-border/50 shadow-card" data-testid={`patient-kintsugi-value-${i + 1}`}>
                <div className={`w-12 h-12 rounded-full ${item.bg} flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`w-6 h-6 ${item.color}`} />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CE QUE VOUS POUVEZ FAIRE ============ */}
      <section className="py-20 px-6 bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4">Ce que vous pouvez faire</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Des outils concrets pour vous accompagner dans votre parcours therapeutique.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((item, i) => (
              <div key={i} className="bg-card rounded-xl border border-border p-6 shadow-card card-hover" data-testid={`patient-benefit-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECURITE ============ */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-card rounded-2xl border border-border p-8 sm:p-10 shadow-card" data-testid="patient-security-section">
            <Lock className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-4">Vos donnees sont en securite</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Kintsugi TCC est heberge sur une infrastructure certifiee <strong>Hebergeur de Donnees de Sante (HDS)</strong>.
              Votre therapeute a signe une charte du secret professionnel et son compte est protege par une double authentification (2FA).
              Vos consentements RGPD sont recueillis et vous pouvez demander la suppression de vos donnees a tout moment.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {["Hebergement HDS", "2FA therapeute", "Consentement RGPD", "Droit a la suppression"].map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <CheckCircle className="w-3.5 h-3.5" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 px-6 bg-primary text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl sm:text-4xl font-semibold mb-4">
            Pret a commencer votre parcours ?
          </h2>
          <p className="text-white/80 mb-8">
            Creez votre compte en quelques secondes et retrouvez les exercices que votre therapeute vous a prepares.
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => navigate(registerUrl)}
            data-testid="patient-cta-register"
            className="bg-white text-primary hover:bg-white/90 gap-2"
          >
            {refCode ? "Rejoindre mon therapeute" : "Creer mon compte"} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-10 w-auto" />
            <span className="font-heading font-semibold">Kintsugi TCC</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link to="/privacy?from=patient" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="patient-footer-privacy">
              Politique de confidentialite
            </Link>
            <Link to="/terms?from=patient" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="patient-footer-terms">
              Conditions d'utilisation
            </Link>
            <Link to="/cgv?from=patient" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="patient-footer-cgv">
              CGV
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">{t("landing.footer")}</p>
        </div>
      </footer>
      <InstallBanner variant="patient-landing" />
    </div>
  );
};

export default PatientLandingPage;
